import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useUIStore } from "../stores/uiStore";
import {
  KEY_BINDINGS, NO_KEYS, WALK_FOV, advance, applyPose, enterPose, exitPose, isTypingTarget,
  look, wheel, type WalkKeys, type WalkPose,
} from "./walk";
import type { BlockerField } from "./blockerField";

export interface OrbitPose { position: THREE.Vector3; target: THREE.Vector3 }

/**
 * ADR 369 — first-person walk. Mounted INSTEAD of OrbitControls while
 * `walkMode` is on; the orbit rig (director, fit, presets) all bail on a null
 * controls ref, so nothing fights the walker.
 *
 * Input: WASD / arrows move on the floor, Q/E (or the wheel) change eye height,
 * Shift runs, X held passes through the blockers (ADR 370). A click on the canvas locks the pointer for mouse look; without
 * the lock, dragging on the canvas looks around (the Playwright harness, and
 * browsers that refuse the lock). Esc releases the pointer — the browser owns
 * that key while locked — and a second Esc leaves the mode.
 *
 * `entry` is where the orbit target was; `exitRef` receives the pose the orbit
 * rig should resume from, applied by RestoreOrbitPose once OrbitControls is
 * back.
 */
export function WalkControls({ entry, exitRef, field, maxHeightM }: {
  entry: OrbitPose;
  exitRef: React.MutableRefObject<OrbitPose | null>;
  /** ADR 370: the warehouse's blocker mask; null = walk through everything. */
  field: BlockerField | null;
  /** Top of the racking, metres; null = the default ceiling. */
  maxHeightM: number | null;
}) {
  const { camera, gl } = useThree();
  const setWalkMode = useUIStore((s) => s.setWalkMode);
  const poseRef = useRef<WalkPose>(enterPose(entry.position, entry.target));
  const fieldRef = useRef<BlockerField | null>(field);
  fieldRef.current = field;
  const maxHeightRef = useRef<number | null>(maxHeightM);
  maxHeightRef.current = maxHeightM;
  const keysRef = useRef<WalkKeys>({ ...NO_KEYS });
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const savedFov = useRef<number | null>(null);

  useEffect(() => {
    const canvas = gl.domElement;
    const cam = camera as THREE.PerspectiveCamera;
    if (typeof cam.fov === "number") {
      savedFov.current = cam.fov;
      cam.fov = WALK_FOV;
      cam.updateProjectionMatrix();
    }
    applyPose(camera, poseRef.current);
    if (import.meta.env.DEV) {
      // ADR 199-style probe: the Playwright harness teleports the walker next to
      // a known rack and reads where it ends up. Dev only.
      const w = window as unknown as { __twinWalk?: unknown };
      w.__twinWalk = {
        get pose() { return { ...poseRef.current }; },
        set pose(p: Partial<WalkPose>) { poseRef.current = { ...poseRef.current, ...p }; },
        get hasField() { return fieldRef.current !== null; },
      };
    }

    const locked = () => document.pointerLockElement === canvas;

    const onKey = (down: boolean) => (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.code === "Escape" && down && !locked()) { setWalkMode(false); return; }
      const k = KEY_BINDINGS[e.code];
      if (!k) return;
      keysRef.current[k] = down;
      if (e.code.startsWith("Arrow") || e.code === "Space") e.preventDefault();
    };
    const keyDown = onKey(true);
    const keyUp = onKey(false);
    const onBlur = () => { keysRef.current = { ...NO_KEYS }; };

    const onClick = () => {
      if (!locked() && canvas.requestPointerLock) {
        try { canvas.requestPointerLock(); } catch { /* not granted: drag-to-look still works */ }
      }
    };
    const onMouseMove = (e: MouseEvent) => {
      if (locked()) {
        poseRef.current = look(poseRef.current, e.movementX, e.movementY);
      } else if (dragRef.current) {
        poseRef.current = look(poseRef.current, e.clientX - dragRef.current.x, e.clientY - dragRef.current.y);
        dragRef.current = { x: e.clientX, y: e.clientY };
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.button === 0 && !locked()) dragRef.current = { x: e.clientX, y: e.clientY };
    };
    const onPointerUp = () => { dragRef.current = null; };
    const onWheel = (e: WheelEvent) => {
      poseRef.current = wheel(poseRef.current, e.deltaY, maxHeightRef.current);
      e.preventDefault();
    };

    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);
    window.addEventListener("blur", onBlur);
    canvas.addEventListener("click", onClick);
    canvas.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", onBlur);
      canvas.removeEventListener("click", onClick);
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("wheel", onWheel);
      if (locked() && document.exitPointerLock) document.exitPointerLock();
      if (savedFov.current !== null) {
        cam.fov = savedFov.current;
        cam.updateProjectionMatrix();
      }
      exitRef.current = exitPose(poseRef.current);
      if (import.meta.env.DEV) delete (window as unknown as { __twinWalk?: unknown }).__twinWalk;
    };
  }, [camera, gl, setWalkMode, exitRef]);

  useFrame((_, dt) => {
    // A tab that was hidden hands back a huge dt; cap it so one frame cannot
    // teleport the walker across the site.
    poseRef.current = advance(poseRef.current, keysRef.current, Math.min(dt, 0.1), fieldRef.current, maxHeightRef.current);
    applyPose(camera, poseRef.current);
  });

  return null;
}
